import { isIpfError } from '@ipfabric/frontend-shared-assets/utils/IpfError';
import { useLoadingState } from '@ipfabric/ui-components/hooks/useLoadingState';
import { withLoadingIndicator } from '@ipfabric/ui-components/utils/loading/withLoadingIndicator';
import {
  API_RESOURCE_CONFLICT,
  type PoliciesWithAttributeScopesPostRequestBody,
  type PoliciesWithAttributeScopesPostResponseBody,
  type PolicyScopeType,
  type PolicyWithApiScopesPutRequestBody,
  type PolicyWithApiScopesPutResponseBody,
  safeAbsurd,
} from '@ipfabric/utils';
import { Button } from 'components/Button/Button';
import { Switch } from 'components/Switch/Switch';
import { useLocale } from 'containers/App/LocaleContext';
import { useState } from 'react';
import type { FC } from 'react';
import { useNotifications } from 'utils/hooks/useNotifications';
import { useApi } from 'utils/request/useApi';
import { ApiPolicyForm } from './ApiPolicyForm';
import { AttributePolicyForm } from './AttributePolicyForm';
import { handlePolicyResourceConflict } from './handlePolicyResourceConflict';
import {
  type ApiPolicyFormValues,
  emptyApiPolicyFormikValues,
  useApiPolicyFormik,
} from './useApiPolicyFormik';
import {
  type AttributePolicyFormValues,
  emptyAttributePolicyFormikValues,
  useAttributePolicyFormik,
} from './useAttributePolicyFormik';

interface Props {
  onCancel: () => void;
}

export const AddPolicy: FC<Props> = (props) => {
  const [scope, setScope] = useState<PolicyScopeType>('apiScopes');
  const api = useApi();
  const [saving, setSaving] = useLoadingState();
  const {
    administrationPolicies: {
      form: policiesFormMsg,
      request: requestMsg,
      scopeType: scopeTypeMsgs,
    },
  } = useLocale();
  const { showNotification } = useNotifications();

  const handleApiSubmit = async (values: ApiPolicyFormValues) => {
    try {
      await withLoadingIndicator(setSaving)(async () =>
        api.request<
          PolicyWithApiScopesPutResponseBody,
          PolicyWithApiScopesPutRequestBody
        >({
          method: 'POST',
          url: 'policies/scopes/api',
          data: values,
        }),
      );

      props.onCancel();
    } catch (e) {
      if (isIpfError(e, 'API') && e.code === API_RESOURCE_CONFLICT) {
        handlePolicyResourceConflict(
          showNotification,
          requestMsg.resourceConflict,
          apiFormik,
        );
      } else {
        throw e;
      }
    }
  };

  const handleAttributeSubmit = async (values: AttributePolicyFormValues) => {
    try {
      await withLoadingIndicator(setSaving)(async () =>
        api.request<
          PoliciesWithAttributeScopesPostResponseBody,
          PoliciesWithAttributeScopesPostRequestBody
        >({
          method: 'POST',
          url: 'policies/scopes/attributes',
          data: values,
        }),
      );

      props.onCancel();
    } catch (e) {
      if (isIpfError(e, 'API') && e.code === API_RESOURCE_CONFLICT) {
        handlePolicyResourceConflict(
          showNotification,
          requestMsg.resourceConflict,
          attributeFormik,
        );
      } else {
        throw e;
      }
    }
  };

  const apiFormik = useApiPolicyFormik({
    initialValues: emptyApiPolicyFormikValues,
    onSubmit: handleApiSubmit,
  });

  const attributeFormik = useAttributePolicyFormik({
    initialValues: emptyAttributePolicyFormikValues,
    onSubmit: handleAttributeSubmit,
  });

  const handleSubmit = () => {
    if (scope === 'apiScopes') {
      apiFormik.submitForm();
    } else if (scope === 'attributeScopes') {
      attributeFormik.submitForm();
    } else {
      safeAbsurd(scope, 'unknown scopeType');
    }
  };

  return (
    <>
      <div className='w-80'>
        <div className='form__label'>{policiesFormMsg.scopeType}</div>
        <Switch<PolicyScopeType>
          options={[
            {
              label: scopeTypeMsgs.attributeScopes,
              value: 'attributeScopes',
              dataTestId: 'attribute-scopes-button',
            },
            {
              label: scopeTypeMsgs.apiScopes,
              value: 'apiScopes',
              dataTestId: 'api-scopes-button',
            },
          ]}
          value={scope}
          onChange={setScope}
        />
      </div>
      {scope === 'apiScopes' && <ApiPolicyForm formik={apiFormik} />}
      {scope === 'attributeScopes' && (
        <AttributePolicyForm formik={attributeFormik} />
      )}
      <div className='mt-8'>
        <Button
          type='button'
          className='mr-2'
          variant='secondary'
          onClick={() => {
            props.onCancel();
          }}
          loading={saving === 'LOADING'}
        >
          {policiesFormMsg.buttons.cancel}
        </Button>
        <Button
          type='button'
          variant='primary'
          onClick={handleSubmit}
          loading={saving === 'LOADING'}
          dataTestId='save-policy-button'
        >
          {policiesFormMsg.buttons.save}
        </Button>
      </div>
    </>
  );
};
